import { ErrorMessage, Field, Form, Formik } from 'formik';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import * as Yup from 'yup';
import AuthShell from '../../components/ui/AuthShell';
import { ROUTES } from '../../config/routes';
import { APIServices } from '../../services/axiosCommon';

const ForgotSchema = Yup.object({ email: Yup.string().email().required('Email is required') });

interface ForgotResponse {
  success: boolean;
  // Dev-only fields the backend returns while real email isn't wired (phase 4).
  data: { ok: boolean; _devKey?: string; _devUid?: number };
}

const ForgotPasswordView = () => {
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [devLink, setDevLink] = useState<string | null>(null);

  return (
    <AuthShell
      title="Reset your password"
      subtitle="Enter your account email — we'll send a reset link."
      footer={
        <>
          Remembered it?{' '}
          <Link to={ROUTES.LOGIN} className="font-medium text-tfs-primary hover:underline">
            Back to login
          </Link>
        </>
      }
    >
      {sentTo ? (
        <div className="space-y-4">
          <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm text-emerald-800">
            If <strong>{sentTo}</strong> is registered, a reset link is on its way.
          </div>
          {devLink && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <p className="font-medium">Dev mode (real email sending ships in phase 4):</p>
              <Link to={devLink} className="mt-1 block break-all font-mono text-[11px] text-amber-900 underline">
                {devLink}
              </Link>
            </div>
          )}
        </div>
      ) : (
        <Formik
          initialValues={{ email: '' }}
          validationSchema={ForgotSchema}
          onSubmit={async (values, { setSubmitting }) => {
            try {
              const res = await APIServices.post<ForgotResponse>('/auth/password-reset/request', values);
              setSentTo(values.email);
              if (res.data._devKey && res.data._devUid) {
                setDevLink(
                  `${ROUTES.RESET_PASSWORD}?membersid=${res.data._devUid}&rkey=${encodeURIComponent(
                    res.data._devKey,
                  )}`,
                );
              }
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {({ isSubmitting }) => (
            <Form className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-700">
                  Email
                </label>
                <Field
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-tfs-primary focus:outline-none focus:ring-1 focus:ring-tfs-primary"
                />
                <ErrorMessage name="email" component="p" className="mt-1 text-xs text-red-600" />
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full rounded-md bg-tfs-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
              >
                {isSubmitting ? 'Sending…' : 'Send reset link'}
              </button>
            </Form>
          )}
        </Formik>
      )}
    </AuthShell>
  );
};

export default ForgotPasswordView;
